import * as AvatarPrimitive from '@radix-ui/react-avatar';
import * as React from 'react';

import { cn } from '@/lib/utils';

import { getAvatarInitials } from './helpers/get-avatar-initials';
import { AVATAR_FALLBACK_TONE, AVATAR_SIZE } from './types/get-avatar-initials-enums';


const AvatarSizeContext = React.createContext<AVATAR_SIZE | undefined>(undefined);

const avatarSizeClasses: Record<AVATAR_SIZE, string> = {
  [AVATAR_SIZE.XS]: 'h-6 w-6',
  [AVATAR_SIZE.SM]: 'h-8 w-8',
  [AVATAR_SIZE.MD]: 'h-10 w-10',
  [AVATAR_SIZE.LG]: 'h-12 w-12',
};

const avatarFallbackTypographyClasses: Record<AVATAR_SIZE, string> = {
  [AVATAR_SIZE.XS]: 'type-micro font-medium',
  [AVATAR_SIZE.SM]: 'type-settings-row-meta font-medium',
  [AVATAR_SIZE.MD]: 'type-field-label font-medium',
  [AVATAR_SIZE.LG]: 'type-body font-medium',
};

interface AvatarProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> {
  size?: AVATAR_SIZE;
}

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  AvatarProps
>(({ className, size, ...props }, ref) => (
  <AvatarSizeContext.Provider value={size}>
    <AvatarPrimitive.Root
      ref={ref}
      className={cn(
        size === undefined
          ? 'relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full'
          : cn(
              'relative flex shrink-0 overflow-hidden rounded-full',
              avatarSizeClasses[size]
            ),
        className
      )}
      {...props}
    />
  </AvatarSizeContext.Provider>
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn('aspect-square h-full w-full', className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

interface AvatarFallbackProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback> {
  name?: string;
  tone?: AVATAR_FALLBACK_TONE;
}

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  AvatarFallbackProps
>(({ children, className, name, tone, ...props }, ref) => {
  const size = React.use(AvatarSizeContext);
  const effectiveSize = size ?? AVATAR_SIZE.MD;
  const isFoundationFallback = size !== undefined || tone !== undefined;
  const effectiveTone =
    tone ?? (isFoundationFallback ? AVATAR_FALLBACK_TONE.NEUTRAL : undefined);

  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      className={cn(
        'flex h-full w-full items-center justify-center rounded-full',
        effectiveTone === AVATAR_FALLBACK_TONE.NEUTRAL &&
          'bg-surface-foundation-subtle text-content-secondary',
        effectiveTone === AVATAR_FALLBACK_TONE.BRAND &&
          'bg-brand-subtle-surface text-brand-subtle-foreground',
        !isFoundationFallback && 'bg-primary/10 text-primary',
        isFoundationFallback && avatarFallbackTypographyClasses[effectiveSize],
        className
      )}
      {...props}
    >
      {children ?? (name === undefined ? undefined : getAvatarInitials(name))}
    </AvatarPrimitive.Fallback>
  );
});
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarFallback, AvatarImage };
